import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Checkbox,
    IconButton,
    Tooltip,
    Alert,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import {
    doc,
    getDoc,
    addDoc,
    collection,
    query,
    setDoc,
    updateDoc,
    where,
    getDocs
} from 'firebase/firestore';

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'textarea', label: 'Paragraph' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'date', label: 'Date' },
    { value: 'checkbox', label: 'Checkbox' },
];

export default function InputDetailFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [responses, setResponses] = useState({});
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [existingSubmission, setExistingSubmission] = useState(null);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [jsonText, setJsonText] = useState('');



    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [status, setStatus] = useState('inactive');

    const [newField, setNewField] = useState({
        label: '',
        placeholder: '',
        fieldType: 'text',
        required: true,
        options: [{ label: '' }],
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title);
                setFormDescription(data.description);
                setFields(data.fields || []);
                setStatus(data.status);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForm = async () => {
        try {
            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (fields.length === 0) {
                setError('Add at least one field');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle,
                description: formDescription,
                formType: 'input-detail',
                fields: fields,
                status: status,
                isPublished: isPublished,
                isLocked: false,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
            } else {
                await setDoc(doc(db, 'forms', `input_${Date.now()}`), formData);
            }

            setError('');
            navigate('/form-builder');
        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form');
        } finally {
            setLoading(false);
        }
    };
    const handleAddOption = () => {
        setNewField({
            ...newField,
            options: [...newField.options, { label: '' }],
        });
    };

    const handleOptionChange = (index, value) => {
        const updated = [...newField.options];
        updated[index].label = value;
        setNewField({ ...newField, options: updated });
    };

    const handleDeleteOption = (index) => {
        const updated = newField.options.filter((_, i) => i !== index);
        setNewField({ ...newField, options: updated });
    };

    const handleAddField = () => {
        if (editingFieldIndex !== null) {
            const updated = [...fields];
            updated[editingFieldIndex] = newField;
            setFields(updated);
        } else {
            setFields([...fields, newField]);
        }

        setNewField({
            label: '',
            placeholder: '',
            fieldType: 'text',
            required: true,
            options: [],
        });
        setEditingFieldIndex(null);
        setDialogOpen(false);
    };

    const handleEditField = (index) => {
        setNewField(fields[index]);
        setEditingFieldIndex(index);
        setDialogOpen(true);
    };
    // const checkIfAlreadySubmitted = async () => {
    //     try {
    //         const q = query(
    //             collection(db, 'submissions'),
    //             where('formId', '==', formId),
    //             where('userId', '==', user.uid)
    //         );

    //         const snapshot = await getDocs(q);

    //         if (!snapshot.empty) {
    //             setAlreadySubmitted(true);
    //         }
    //     } catch (err) {
    //         console.error('Error checking submission:', err);
    //     }
    // };

    const handleDeleteField = (index) => {
        if (window.confirm('Delete this field?')) {
            setFields(fields.filter((_, i) => i !== index));
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Form Header */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#333' }}>
                    📝 {formId && formId !== 'new' ? 'Edit' : 'Create'} Input Detail Form
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Form Title"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            placeholder="Enter form title"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            placeholder="Enter form description"
                            multiline
                            rows={3}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={isPublished ? 'Published' : 'Unpublished'}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setJsonInput('');
                            setJsonDialogOpen(true);
                        }}
                    >
                        Import JSON
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/form-builder')}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveForm}
                        disabled={loading}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {loading ? 'Saving...' : 'Save Form'}
                    </Button>
                </Box>
            </Paper>

            {/* Fields Section */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                        🗂️ Fields ({fields.length})
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setNewField({
                                label: '',
                                placeholder: '',
                                fieldType: 'text',
                                required: true,
                                options: [],
                            });
                            setEditingFieldIndex(null);
                            setDialogOpen(true);
                        }}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        Add Field
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {fields.map((field, index) => (
                        <Grid item xs={12} key={index}>
                            <Card sx={{ borderLeft: '4px solid #667eea' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        {index + 1}. {field.label}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        {field.placeholder && `Placeholder: "${field.placeholder}"`}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={
                                                FIELD_TYPES.find((t) => t.value === field.fieldType)?.label ||
                                                field.fieldType
                                            }
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={field.required ? 'Required' : 'Optional'}
                                            size="small"
                                            color={field.required ? 'warning' : 'default'}
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditField(index)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteField(index)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {fields.length === 0 && (
                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                        No fields added yet. Click "Add Field" to get started.
                    </Typography>
                )}
            </Paper>

            {/* Field Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingFieldIndex !== null ? 'Edit Field' : 'Add Field'}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Field Label"
                        value={newField.label}
                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                    />

                    <TextField
                        fullWidth
                        label="Placeholder Text"
                        value={newField.placeholder}
                        onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Field Type</InputLabel>
                        <Select
                            value={newField.fieldType}
                            onChange={(e) => setNewField({ ...newField, fieldType: e.target.value })}
                            label="Field Type"
                        >
                            {FIELD_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={newField.required}
                                onChange={(e) =>
                                    setNewField({ ...newField, required: e.target.checked })
                                }
                            />
                        }
                        label="Required"
                    />

                    {['dropdown', 'checkbox'].includes(newField.fieldType) && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Options
                            </Typography>

                            {newField.options.map((option, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={`Option ${index + 1}`}
                                        value={option.label}
                                        onChange={(e) =>
                                            handleOptionChange(index, e.target.value)
                                        }
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteOption(index)}
                                        disabled={newField.options.length === 1}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}

                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddOption}
                                fullWidth
                                sx={{ mt: 1 }}
                            >
                                Add Option
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddField}
                        variant="contained"
                        disabled={!newField.label}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        {editingFieldIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* -------- JSON IMPORT DIALOG -------- */}
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Import Fields from JSON</DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        label="Paste JSON here"
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                    />

                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={() => {
                            try {
                                const parsed = JSON.parse(jsonText);

                                if (!Array.isArray(parsed.fields)) {
                                    setError('JSON must contain fields array');
                                    return;
                                }

                                setFields(parsed.fields);
                                setJsonDialogOpen(false);
                                setJsonText('');
                            } catch (err) {
                                setError('Invalid JSON format');
                            }
                        }}
                    >
                        Import
                    </Button>
                </DialogContent>
            </Dialog>

        </Container>
    );
}
